import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';

import { isSearchAutoRefreshFlagActive } from '../../../utils/features';
import HomeModel from '../../../models/Home';
import Tour from '../../../utils/Tour';
import i18n from '../../../i18n';
import Screen from '../../../utils/Screen';

import MapComponent from '../../common/map/mapComponent';
import Home from '../../home/Home';

import CustomMarker from './CustomMarker';
import SpinnerComponent from './Spinner/SpinnerComponent';
import PinLegends from './PinLegends';

const mapResults = (
    data,
    selectedId,
    isLogged,
    homeFavorites,
    isAdditional = false,
    openPopupId,
    setOpenPopupId
) =>
    data?.map((result) => {
        const latitude = result.get('location')?.latitude;
        const longitude = result.get('location')?.longitude;

        return {
            id: result?.get('id'),
            position: {
                lat: latitude,
                lon: longitude
            },
            customMarker: (
                <CustomMarker
                    latitude={latitude}
                    longitude={longitude}
                    isFavorite={result.isFavorite(homeFavorites)}
                    isAdditional={isAdditional}
                    isSelected={selectedId === result?.get('id')}
                    popupContent={
                        <Home
                            home={result}
                            displayWithReview={true}
                            isLogged={isLogged}
                            tooltipBadgeVerified={false}
                            displayFavoritePlaceholder={false}
                            displayCarousel={false}
                            displayLocation={Screen.noDesktop()}
                            displayProfilePicture={true}
                            source="map"
                        />
                    }
                    openPopupId={openPopupId}
                    setOpenPopupId={setOpenPopupId}
                    markerId={result?.get('id')}
                />
            )
        };
    });

const SearchMap = forwardRef(
    (
        {
            results,
            additionalResults,
            hoveredHomeId,
            bounds,
            searchInProcess = false,
            onBoundsChange,
            displaySearchInThisRegionBtn,
            updateDisplaySearchInThisRegionBtnStatus,
            updateDisplayAlertsStatus,
            isLogged,
            isMapDisplayed,
            displayMap,
            homeFavorites
        },
        ref
    ) => {
        const mapRef = useRef();
        const refreshSearchBtnRef = useRef();
        const [markerList, setMarkerList] = useState([]);
        const [resultList, setResultList] = useState([]);
        const [additionalList, setAdditionalList] = useState([]);
        const [mapBounds, setMapBounds] = useState(bounds);
        const [displayedBounds, setDisplayedBounds] = useState();
        const [shouldRefresh, setShouldRefresh] = useState({
            unzoom: false,
            clickZoom: false
        });
        const [openPopupId, setOpenPopupId] = useState(null);

        const intervalRef = useRef(null);
        const enableAutoRefresh = isSearchAutoRefreshFlagActive();
        const timeToRefresh = 1300;

        const [shouldDisplaySpinner, setShouldDisplaySpinner] = useState(false);
        const buttonVisibilityStyle = enableAutoRefresh ? 'none' : 'block';

        const zoomAndUpdate = (scale) => {
            if (mapRef.current) {
                const zoomLvl = mapRef.current.getZoom();
                mapRef.current.zoomTo(zoomLvl - scale);
                setShouldRefresh({ ...shouldRefresh, clickZoom: true });
            }
        };

        const updateBounds = (newBounds) => {
            setMapBounds({
                ne: {
                    lat: parseFloat(newBounds._ne.lat),
                    lon: parseFloat(newBounds._ne.lng)
                },
                sw: {
                    lat: parseFloat(newBounds._sw.lat),
                    lon: parseFloat(newBounds._sw.lng)
                }
            });
        };

        const stopInterval = () => {
            setShouldDisplaySpinner(false);
            clearInterval(intervalRef.current);
        };

        const lunchRefreshSearch = _.debounce(() => {
            if (isMapDisplayed() === false) {
                stopInterval();
                return;
            }
            if (displaySearchInThisRegionBtn && enableAutoRefresh && isMapDisplayed()) {
                clearInterval(intervalRef.current);
                intervalRef.current = setInterval(() => refreshSearchBtnRef?.current?.click(), timeToRefresh);
            }
        }, timeToRefresh);

        const refreshSearch = () => {
            if (onBoundsChange && mapBounds) {
                try {
                    onBoundsChange(mapBounds);
                    updateDisplayAlertsStatus(false);
                    setShouldDisplaySpinner(false);
                    setShouldRefresh({ clickZoom: false, unzoom: false });
                } catch (error) {
                    console.error('update error => ', error);
                }
            }
        };

        const handleMapMove = (e) => {
            if (e.originalEvent) {
                updateDisplaySearchInThisRegionBtnStatus(true);
                setShouldDisplaySpinner(true);
            }
            updateBounds(e.bounds);
            lunchRefreshSearch();
        };

        const handleZoomEnd = (e) => {
            if (e.originalEvent) {
                updateDisplaySearchInThisRegionBtnStatus(true);
                setShouldDisplaySpinner(true);
            }
            updateBounds(e.bounds);
            setShouldRefresh({ ...shouldRefresh, unzoom: true });
            lunchRefreshSearch();
        };

        useEffect(() => {
            setMarkerList([...resultList, ...additionalList]);
        }, [resultList, additionalList]);

        useEffect(() => {
            if (results?.length > 0) {
                const markers = mapResults(
                    results,
                    hoveredHomeId,
                    isLogged,
                    homeFavorites,
                    false,
                    openPopupId,
                    setOpenPopupId
                );
                setResultList(markers);
            } else {
                setResultList([]);
            }
        }, [results, hoveredHomeId, openPopupId]);

        useEffect(() => {
            if (additionalResults?.length > 0) {
                const markers = mapResults(
                    additionalResults,
                    hoveredHomeId,
                    isLogged,
                    homeFavorites,
                    true,
                    openPopupId,
                    setOpenPopupId
                );
                setAdditionalList(markers);
            } else {
                setAdditionalList([]);
            }
        }, [additionalResults, hoveredHomeId, openPopupId]);

        useEffect(() => {
            if (displaySearchInThisRegionBtn) {
                Tour.init(Tour.updateSearchCtaTour.id);
            }
        }, [displaySearchInThisRegionBtn]);

        useImperativeHandle(ref, () => ({
            unzoom(scale) {
                zoomAndUpdate(scale);
            }
        }));

        useEffect(() => {
            if (shouldRefresh.clickZoom && shouldRefresh.unzoom) {
                updateDisplaySearchInThisRegionBtnStatus(true);
                refreshSearch();
            }
        }, [shouldRefresh]);

        useEffect(() => {
            if (bounds && !_.isEqual(bounds, displayedBounds)) {
                setDisplayedBounds(bounds);
            }
        }, [bounds]);

        useEffect(() => {
            if (displaySearchInThisRegionBtn) {
                lunchRefreshSearch();
            } else {
                stopInterval();
            }
        }, [displaySearchInThisRegionBtn]);

        useEffect(() => {
            if (!searchInProcess) {
                return;
            }
            setShouldDisplaySpinner(searchInProcess && enableAutoRefresh && displaySearchInThisRegionBtn);
        }, [searchInProcess, enableAutoRefresh, displaySearchInThisRegionBtn]);

        useEffect(() => {
            if (!displayMap) {
                stopInterval();
            }
        }, [displayMap]);

        return displayedBounds ? (
            <>
                <PinLegends />
                {shouldDisplaySpinner && <SpinnerComponent />}
                {displaySearchInThisRegionBtn && (
                    <button
                        className={classNames('btn', 'btn-primary', 'search-in-this-region', {
                            hidden: enableAutoRefresh
                        })}
                        onClick={refreshSearch}
                        ref={refreshSearchBtnRef}
                        style={{ display: buttonVisibilityStyle }}
                    >
                        <span
                            className={classNames('icon-reload-white', {
                                'icon-reload-white-rotate': searchInProcess
                            })}
                        />
                        {i18n.t('search:search-map.search-in-this-region')}
                    </button>
                )}
                <MapComponent
                    ref={mapRef}
                    bounds={displayedBounds}
                    markerList={markerList}
                    onMoveStart={stopInterval}
                    onMoveEnd={handleMapMove}
                    showControls
                    controlPosition="top-left"
                    onZoomEnd={handleZoomEnd}
                    onZoomStart={stopInterval}
                />
            </>
        ) : null;
    }
);

SearchMap.propTypes = {
    bounds: PropTypes.shape({
        bounds: PropTypes.shape({
            sw: PropTypes.shape({
                lat: PropTypes.number.isRequired,
                lon: PropTypes.number.isRequired
            }).isRequired,
            ne: PropTypes.shape({
                lat: PropTypes.number.isRequired,
                lon: PropTypes.number.isRequired
            }).isRequired
        })
    }),
    results: PropTypes.arrayOf(PropTypes.instanceOf(HomeModel)),
    additionalResults: PropTypes.arrayOf(PropTypes.instanceOf(HomeModel)),
    hoveredHomeId: PropTypes.number,
    searchInProcess: PropTypes.bool,
    onBoundsChange: PropTypes.any,
    displaySearchInThisRegionBtn: PropTypes.any,
    updateDisplaySearchInThisRegionBtnStatus: PropTypes.any,
    updateDisplayAlertsStatus: PropTypes.func,
    isLogged: PropTypes.bool,
    isMapDisplayed: PropTypes.func,
    displayMap: PropTypes.bool,
    homeFavorites: PropTypes.arrayOf(PropTypes.number)
};

export default SearchMap;
